import React, { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Container,
  Paper,
  useTheme,
  Avatar,
  Chip,
  Stack
} from '@mui/material'
import {
  Visibility,
  VisibilityOff,
  Email,
  Lock,
  Business,
  Login as LoginIcon,
  Receipt,
  TrendingUp,
  Security,
  Speed
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'

import useAuthStore from '../../store/authStore'
import { authAPI, formatApiError } from '../../utils/api'

// Validation schema
const loginSchema = yup.object({
  email: yup
    .string()
    .email('Ingresa un email válido')
    .required('El email es requerido'),
  password: yup
    .string()
    .min(6, 'La contraseña debe tener al menos 6 caracteres')
    .required('La contraseña es requerida')
})

const LoginPage = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const { enqueueSnackbar } = useSnackbar()
  const { login, setLoading, isLoading } = useAuthStore()
  
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')

  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(loginSchema),
    defaultValues: {
      email: '',
      password: ''
    }
  })

  const onSubmit = async (data) => {
    try {
      setError('')
      setLoading(true)

      const response = await authAPI.login(data)
      const { user, token } = response.data

      login(user, token)
      
      enqueueSnackbar('Inicio de sesión exitoso', { variant: 'success' })
      
      // Redirect to intended page or dashboard
      const from = location.state?.from?.pathname || '/dashboard'
      navigate(from, { replace: true })
    } catch (err) {
      const errorMessage = formatApiError(err)
      setError(errorMessage)
      enqueueSnackbar(errorMessage, { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        height: '100vh',
        display: 'flex',
        position: 'relative',
        overflow: 'hidden',
        background: `linear-gradient(135deg, ${theme.palette.primary.main}08 0%, ${theme.palette.secondary.main}08 50%, ${theme.palette.primary.light}08 100%)`,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23f0f0f0" fill-opacity="0.03"%3E%3Ccircle cx="30" cy="30" r="1"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          zIndex: 0
        }
      }}
    >
      <Container 
        maxWidth="lg" 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          py: { xs: 2, md: 4 },
          px: { xs: 2, md: 3 },
          position: 'relative',
          zIndex: 1,
          width: '100%',
          height: '100%',
          overflow: 'auto'
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          width: '100%', 
          maxWidth: 900, 
          minHeight: { xs: 'auto', md: '500px' },
          height: { xs: 'auto', md: '500px' },
          maxHeight: { xs: 'none', md: '90vh' }
        }}>
          
          {/* Left Side - Branding & Features */}
          <Box
            sx={{
              flex: 1,
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              justifyContent: 'center',
              p: 4,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: 'white',
              borderTopLeftRadius: 20,
              borderBottomLeftRadius: 20,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
                background: 'url("data:image/svg+xml,%3Csvg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Cpath d="M20 20c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10zm10 0c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10z"/%3E%3C/g%3E%3C/svg%3E")',
                zIndex: 0
              }
            }}
          >
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                {/* Logo */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar
                    sx={{
                      width: 50,
                      height: 50,
                      bgcolor: 'rgba(255,255,255,0.2)',
                      mr: 2,
                      backdropFilter: 'blur(10px)'
                    }}
                  >
                    <Receipt sx={{ fontSize: 24 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight={700} sx={{ mb: 0.5 }}>
                      Yotta
                    </Typography>
                    <Typography variant="subtitle1" sx={{ opacity: 0.9, fontWeight: 400 }}>
                      Control de Pagos
                    </Typography>
                  </Box>
                </Box>

                {/* Description */}
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 300, lineHeight: 1.3 }}>
                  Gestiona tus facturas por cobrar de manera inteligente y eficiente
                </Typography>

                {/* Features */}
                <Stack spacing={2} sx={{ mt: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(255,255,255,0.2)', mr: 1.5 }}>
                      <TrendingUp fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="body1" fontWeight={600}>
                        Dashboard Inteligente
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8, fontSize: '0.8rem' }}>
                        KPIs y métricas en tiempo real
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(255,255,255,0.2)', mr: 1.5 }}>
                      <Security fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="body1" fontWeight={600}>
                        Seguridad Avanzada
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8, fontSize: '0.8rem' }}>
                        Control de acceso y encriptación
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(255,255,255,0.2)', mr: 1.5 }}>
                      <Speed fontSize="small" />
                    </Avatar>
                    <Box>
                      <Typography variant="body1" fontWeight={600}>
                        Automatización
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8, fontSize: '0.8rem' }}>
                        Notificaciones automáticas
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Box>
            </Box>

          {/* Right Side - Login Form */}
          <Paper
              elevation={0}
              sx={{
                flex: { xs: 1, md: 0.6 },
                display: 'flex',
                flexDirection: 'column',
                borderTopRightRadius: 20,
                borderBottomRightRadius: 20,
                borderTopLeftRadius: { xs: 20, md: 0 },
                borderBottomLeftRadius: { xs: 20, md: 0 },
                background: 'background.paper',
                backdropFilter: 'blur(20px)',
                border: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                maxHeight: '100%',
                width: '100%'
              }}
            >
              {/* Mobile Header */}
              <Box
                sx={{
                  display: { xs: 'block', md: 'none' },
                  p: 2,
                  textAlign: 'center',
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  color: 'white',
                  flexShrink: 0
                }}
              >
                <Avatar
                  sx={{
                    width: 40,
                    height: 40,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    mx: 'auto',
                    mb: 1.5
                  }}
                >
                  <Receipt sx={{ fontSize: 20 }} />
                </Avatar>
                <Typography variant="h6" fontWeight={700}>
                  Yotta Control de Pagos
                </Typography>
              </Box>

              {/* Form Content */}
              <Box sx={{ 
                flex: 1, 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: { xs: 'flex-start', md: 'center' }, 
                p: { xs: 2, md: 3 },
                overflow: 'auto',
                minHeight: 0
              }}>
                {/* Welcome Message */}
                <Box sx={{ mb: { xs: 2, md: 3 }, textAlign: 'center' }}>
                  <Typography variant="h5" fontWeight={600} gutterBottom color="primary">
                    ¡Bienvenido!
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Ingresa tus credenciales para acceder al sistema
                  </Typography>
                  
                  {/* Status Indicators */}
                  <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 1.5 }}>
                    <Chip 
                      label="Seguro" 
                      size="small" 
                      color="success" 
                      variant="outlined"
                      icon={<Security fontSize="small" />}
                    />
                    <Chip 
                      label="Rápido" 
                      size="small" 
                      color="info" 
                      variant="outlined"
                      icon={<Speed fontSize="small" />}
                    />
                  </Stack>
                </Box>

                {/* Error Alert */}
                {error && (
                  <Alert 
                    severity="error" 
                    sx={{ mb: 3, borderRadius: 2 }}
                    onClose={() => setError('')}
                  >
                    {error}
                  </Alert>
                )}

                {/* Login Form */}
                <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ width: '100%' }}>
                  {/* Email Field */}
                  <TextField
                    {...register('email')}
                    fullWidth
                    label="Correo Electrónico"
                    type="email"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    disabled={isLoading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Email color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{ 
                      mb: { xs: 2, md: 2.5 },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }
                      }
                    }}
                  />

                  {/* Password Field */}
                  <TextField
                    {...register('password')}
                    fullWidth
                    label="Contraseña"
                    type={showPassword ? 'text' : 'password'}
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    disabled={isLoading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleTogglePasswordVisibility}
                            edge="end"
                            disabled={isLoading}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ 
                      mb: { xs: 2.5, md: 3 },
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                        }
                      }
                    }}
                  />

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={isLoading}
                    startIcon={
                      isLoading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <LoginIcon />
                      )
                    }
                    sx={{
                      py: 1.5,
                      fontSize: '1rem',
                      fontWeight: 600,
                      borderRadius: 3,
                      textTransform: 'none',
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      boxShadow: '0 3px 10px rgba(25, 118, 210, 0.3)',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        boxShadow: '0 4px 15px rgba(25, 118, 210, 0.4)',
                        transform: 'translateY(-1px)'
                      },
                      '&:disabled': {
                        background: 'action.disabledBackground',
                        boxShadow: 'none',
                        transform: 'none'
                      }
                    }}
                  >
                    {isLoading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
                  </Button>
                </Box>

                {/* Footer */}
                <Box sx={{ mt: { xs: 2, md: 3 }, textAlign: 'center', flexShrink: 0 }}>
                  <Typography variant="caption" color="text.secondary">
                    © 2024 Yotta. Sistema seguro y confiable.
                  </Typography>
                </Box>
              </Box>
            </Paper>
        </Box>
      </Container>
    </Box>
  )
}

export default LoginPage
